import { Alert, Paper, Stack, Typography } from "@mui/material";
import { isApiError } from "@bw/shared/api";
import { AccountNameEditor } from "../../../common/components/AccountNameEditor";
import type { UserProfileUpdate } from "@bw/shared/types/generated";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Page } from "../../../common/components/Page";
import {
  MaterialAreaPicker,
  type MaterialAreaCenter,
} from "../../../common/components/MaterialAreaPicker";
import { useMyProfile } from "../../../common/query/useMyProfile";
import { useUpdateProfile } from "../../../common/query/useUpdateProfile";
import { ProfileEditForm } from "../components/ProfileEditForm";

export function ProfileEditPage() {
  const navigate = useNavigate();
  const profile = useMyProfile();
  const updateProfile = useUpdateProfile();

  const handleSubmit = (body: UserProfileUpdate) => {
    updateProfile.mutate(body, {
      onSuccess: () => navigate("/me", { replace: true }),
    });
  };
  const handleAreaSelect = (area: MaterialAreaCenter) => {
    updateProfile.mutate({
      home_area_label: area.label,
      home_latitude: area.lat,
      home_longitude: area.lng,
    });
  };
  const errorMessage = updateProfile.isError
    ? isApiError(updateProfile.error)
      ? updateProfile.error.status === 404
        ? "We couldn't find your profile. Try refreshing the page."
        : updateProfile.error.message ||
          "Something went wrong. Please try again."
      : "Something went wrong. Please try again."
    : null;
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (errorMessage) errorRef.current?.focus();
  }, [errorMessage]);

  return (
    <Page title="Edit profile">
      <Stack spacing={2}>
        <AccountNameEditor />
        {profile.isLoading ? (
          <Typography color="text.secondary" role="status">
            Loading your profile…
          </Typography>
        ) : profile.isError || !profile.data ? (
          <Alert severity="error" role="alert">
            We couldn&apos;t load your profile. Please try again.
          </Alert>
        ) : (
          <>
            {errorMessage ? (
              <Alert ref={errorRef} severity="error" role="alert" tabIndex={-1}>
                {errorMessage}
              </Alert>
            ) : null}
            <ProfileEditForm
              profile={profile.data}
              onSubmit={handleSubmit}
              submitting={updateProfile.isPending}
              onCancel={() => navigate("/me")}
            />
            <Paper
              component="section"
              aria-labelledby="me-home-area"
              variant="outlined"
              sx={{ p: 2.5 }}
            >
              <Stack spacing={1.5}>
                <Typography id="me-home-area" component="h2" variant="h6">
                  Home area
                </Typography>
                <Typography color="text.secondary">
                  Set a city or area to sort courts and drop-in. Your exact
                  location is never saved.
                </Typography>
                {profile.data.home_area_label ? (
                  <Typography role="status">
                    Saved area: {profile.data.home_area_label}
                  </Typography>
                ) : null}
                <MaterialAreaPicker
                  label="Home area"
                  placeholder="Search a city or area"
                  onAreaSelect={handleAreaSelect}
                />
              </Stack>
            </Paper>
          </>
        )}
      </Stack>
    </Page>
  );
}
